// Perceptual mic-level meter: aggregates raw RMS into ~50 ms windows,
// applies VU-style fast-attack / slow-release smoothing, and maps to a
// 0–100 display scale in decibels (linear RMS makes normal speech look
// tiny and makes the needle twitch between glottal pulses).
//
// Scale anchors (with browser AGC/NS on):
//   silence / room noise  rms ≲ 0.003  → ~0–8 %
//   quiet speech          rms ≈ 0.01   → ~20 %
//   normal speech         rms ≈ 0.05   → ~48 %
//   loud speech           rms ≈ 0.15   → ~67 %

export const SPEECH_LEVEL_PCT = 25; // display % treated as "speech present"

export function rmsToPct(rms: number): number {
  if (rms <= 0) return 0;
  const db = 20 * Math.log10(rms); // dBFS, negative
  const pct = ((db + 50) / 50) * 100; // -50 dB → 0 %, 0 dB → 100 %
  return Math.max(0, Math.min(100, Math.round(pct)));
}

export class LevelSmoother {
  private sumSq = 0;
  private nSamples = 0;
  private smoothedRms = 0;

  constructor(
    private sampleRate: number,
    /** Aggregation window in ms (default 50). */
    private windowMs = 50,
    /** Fraction of the gap closed per window when rising (fast attack). */
    private attack = 0.6,
    /** Fraction of the gap closed per window when falling (slow release). */
    private release = 0.15,
  ) {}

  /**
   * Feed one audio block's RMS. Returns the new display percentage when
   * a window completed, or null while still accumulating.
   */
  push(blockRms: number, blockSamples: number): number | null {
    this.sumSq += blockRms * blockRms * blockSamples;
    this.nSamples += blockSamples;
    if (this.nSamples < (this.sampleRate * this.windowMs) / 1000) return null;
    const windowRms = Math.sqrt(this.sumSq / this.nSamples);
    this.sumSq = 0;
    this.nSamples = 0;
    const k = windowRms > this.smoothedRms ? this.attack : this.release;
    this.smoothedRms += (windowRms - this.smoothedRms) * k;
    return rmsToPct(this.smoothedRms);
  }
}
